import { Fragment, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { stageLabel, prioLabel, roleLabel, slaInfo, fmtDate } from "@/lib/constants";

const STAGE_BG = { abertura: "bg-info", t1_triagem: "bg-warning", t2_atendimento_interno: "bg-purple", t3_responsavel_tecnico: "bg-indigo", resolucao: "bg-success" };
const PRIO_BG = { baixa: "bg-secondary", media: "bg-primary", alta: "bg-orange", critica: "bg-danger" };
const SLA_BG = { ok: "bg-success", warning: "bg-warning", breached: "bg-danger", met: "bg-success", missed: "bg-secondary" };
export const stageBg = (k) => STAGE_BG[k] || "bg-secondary";
export const slaBg = (k) => SLA_BG[k] || "bg-secondary";

export const StageBadge = ({ stage, testId }) => (
  <span data-testid={testId} className={`badge ${stageBg(stage)}`}>{stageLabel(stage)}</span>
);

export const PrioBadge = ({ prio, testId }) => (
  <span data-testid={testId} className={`badge ${PRIO_BG[prio] || "bg-secondary"}`}>{prioLabel(prio)}</span>
);

export const SlaBadge = ({ ticket, now, testId }) => {
  const s = slaInfo(ticket, now);
  const icon = s.state === "breached" || s.state === "missed" ? "fas fa-exclamation-triangle" : "far fa-clock";
  return (
    <span data-testid={testId} data-sla-state={s.state} title={`Prazo: ${fmtDate(s.deadline)}`} className={`badge ${slaBg(s.state)}`}>
      <i className={`${icon} mr-1`} />{s.label}
    </span>
  );
};

// Content Header + section.content (estrutura padrão das páginas do AdminLTE)
export const Page = ({ title, crumbs = [], children }) => (
  <>
    <div className="content-header">
      <div className="container-fluid">
        <div className="row mb-2">
          <div className="col-sm-6">
            <h1 className="m-0" data-testid="page-title">{title}</h1>
          </div>
          <div className="col-sm-6">
            <ol className="breadcrumb float-sm-right">
              <li className="breadcrumb-item"><Link to="/">Home</Link></li>
              {crumbs.map(([label, to], i) => (
                <li key={i} className={`breadcrumb-item ${to ? "" : "active"}`}>{to ? <Link to={to}>{label}</Link> : label}</li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </div>
    <section className="content">
      <div className="container-fluid">{children}</div>
    </section>
  </>
);

export const Card = ({ title, icon, tools, children, className = "", bodyClass = "", testId, footer }) => (
  <div data-testid={testId} className={`card ${className}`}>
    {title && (
      <div className="card-header">
        <h3 className="card-title">{icon && <i className={`${icon} mr-1`} />}{title}</h3>
        {tools && <div className="card-tools">{tools}</div>}
      </div>
    )}
    <div className={`card-body ${bodyClass}`}>{children}</div>
    {footer && <div className="card-footer">{footer}</div>}
  </div>
);

export const Empty = ({ text, testId }) => (
  <p data-testid={testId} className="text-center text-muted my-4">{text}</p>
);

// Gráfico Chart.js 2.9.4 (mesma biblioteca e versão usadas pelo AdminLTE 3)
export const ChartCanvas = ({ config, height = 250, testId }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!window.Chart || !ref.current) return undefined;
    const chart = new window.Chart(ref.current.getContext("2d"), config);
    return () => chart.destroy();
  }, [config]);
  return (
    <div className="position-relative mb-2" style={{ height }} data-testid={testId}>
      <canvas ref={ref} height={height} />
    </div>
  );
};

export const readFileAsDataUrl = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res({ name: file.name, content_type: file.type || "application/octet-stream", data_url: r.result });
    r.onerror = rej;
    r.readAsDataURL(file);
  });

export const useNow = (ms = 30000) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), ms); return () => clearInterval(i); }, [ms]);
  return now;
};

// Modal do Bootstrap 4 controlado pelo plugin nativo ($(el).modal), com as mesmas animações,
// backdrop, Esc e data-dismiss="modal". onClose é chamado após o evento hidden.bs.modal;
// o ref expõe hide(callback) para fechar com animação a partir do código (ex.: após salvar).
export const Modal = forwardRef(({ title, onClose, children, footer, size = "", testId }, ref) => {
  const el = useRef(null);
  const afterHide = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const $ = window.jQuery;
    if (!$?.fn?.modal) return undefined;
    const $el = $(el.current);
    $el.on("hidden.bs.modal", () => {
      const cb = afterHide.current;
      afterHide.current = null;
      onCloseRef.current?.();
      cb?.();
    });
    // setTimeout: no StrictMode o efeito monta/desmonta/monta; assim o modal abre uma única vez
    const timer = setTimeout(() => $el.modal({ show: true }), 0);
    return () => {
      clearTimeout(timer);
      $el.off("hidden.bs.modal");
      // Desmontado ainda aberto (ex.: troca de rota): limpa o que o plugin colocou no body
      if ($el.data("bs.modal")?._isShown) {
        $(".modal-backdrop").remove();
        $("body").removeClass("modal-open").css("padding-right", "");
      }
      $el.removeData("bs.modal");
    };
  }, []);

  const hide = useCallback((cb) => {
    afterHide.current = cb || null;
    window.jQuery?.(el.current).modal("hide");
  }, []);
  useImperativeHandle(ref, () => ({ hide }), [hide]);

  return createPortal(
    <div ref={el} className="modal fade" tabIndex={-1} role="dialog" aria-hidden="true" data-testid={testId}>
      <div className={`modal-dialog ${size}`} role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">{title}</h4>
            <button type="button" className="close" data-dismiss="modal" aria-label="Fechar"><span aria-hidden="true">&times;</span></button>
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer justify-content-between">{footer}</div>}
        </div>
      </div>
    </div>,
    document.body,
  );
});

const ACTIONS = {
  abertura: ["Chamado aberto", "fas fa-plus", "bg-blue"],
  transferencia: ["Transferência", "fas fa-exchange-alt", "bg-yellow"],
  comentario: ["Comentário", "fas fa-comments", "bg-info"],
  resolucao: ["Resolvido", "fas fa-check", "bg-green"],
  reabertura: ["Reaberto", "fas fa-redo", "bg-orange"],
  evidencia_anexada: ["Evidência anexada", "fas fa-paperclip", "bg-purple"],
  prioridade: ["Prioridade alterada", "fas fa-flag", "bg-red"],
  redmine: ["Enviado para o Redmine", "fas fa-share-square", "bg-maroon"],
};

const historyBody = (h) => {
  const d = h.details || {};
  if (h.action === "transferencia") return <>{stageLabel(d.from_stage)} &rarr; <b>{stageLabel(d.to_stage)}</b>{d.assigned_to && ` · ${d.assigned_to}`}{d.nota && <div className="font-italic mt-1">“{d.nota}”</div>}</>;
  if (h.action === "prioridade") return <>{prioLabel(d.from_prioridade)} &rarr; <b>{prioLabel(d.to_prioridade)}</b>{d.nota && <div className="font-italic mt-1">“{d.nota}”</div>}</>;
  if (h.action === "resolucao") return <span className="font-italic">{d.solucao}</span>;
  if (h.action === "redmine") return <><a href={d.url} target="_blank" rel="noopener noreferrer"><b>#{d.issue_id}</b></a> &middot; {d.titulo}</>;
  if (h.action === "comentario" && d.interno) return <span className="text-muted"><i className="fas fa-lock mr-1" />Nota interna</span>;
  return null;
};

// Timeline do AdminLTE (pages/UI/timeline.html), agrupada por dia
export const Timeline = ({ items }) => {
  let lastDay;
  return (
    <div className="timeline" data-testid="ticket-timeline">
      {[...items].reverse().map((h) => {
        const dt = new Date(h.timestamp);
        const day = dt.toLocaleDateString("pt-BR");
        const showDay = day !== lastDay;
        lastDay = day;
        const [title, icon, bg] = ACTIONS[h.action] || [h.action, "fas fa-circle", "bg-gray"];
        const body = historyBody(h);
        return (
          <Fragment key={h.id}>
            {showDay && <div className="time-label"><span className="bg-primary">{day}</span></div>}
            <div>
              <i className={`${icon} ${bg}`} />
              <div className="timeline-item">
                <span className="time"><i className="fas fa-clock" /> {dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
                <h3 className="timeline-header"><b>{h.actor_name}</b> <small className="text-muted">({roleLabel(h.actor_role)})</small> &middot; {title}</h3>
                {body && <div className="timeline-body">{body}</div>}
              </div>
            </div>
          </Fragment>
        );
      })}
      <div><i className="fas fa-clock bg-gray" /></div>
    </div>
  );
};

// Logo do brand-link (AdminLTE: img.brand-image). Sem imagem enviada, mostra a inicial do nome.
// Com o título do sidebar desligado, a logo fica maior e centralizada (.brand-logo-only em index.css)
export const BrandLogo = ({ skin }) => {
  const box = skin.brandShowTitle === false ? 45 : 33;
  const shape = skin.brandLogoCircle ? "img-circle" : "";
  if (skin.brandLogo) return <img src={skin.brandLogo} alt={skin.brandName} className={`brand-image ${shape} elevation-3`} style={{ opacity: 0.8 }} data-testid="brand-logo" />;
  return (
    <span className={`brand-image ${shape} elevation-3 bg-primary text-center`} style={{ opacity: 0.8, width: box, height: box, lineHeight: `${box}px` }} data-testid="brand-logo">
      <b>{(skin.brandName || "?").trim().charAt(0).toUpperCase()}</b>
    </span>
  );
};

// Redimensiona a imagem no navegador (lado maior = max px) e devolve um data URL PNG
export const resizeImage = (file, max = 128) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onerror = rej;
    r.onload = () => {
      const img = new Image();
      img.onerror = () => rej(new Error("Imagem inválida"));
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width || max, img.height || max));
        const c = document.createElement("canvas");
        c.width = Math.round((img.width || max) * scale);
        c.height = Math.round((img.height || max) * scale);
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        res(c.toDataURL("image/png"));
      };
      img.src = r.result;
    };
    r.readAsDataURL(file);
  });

// Borda pulsante (index.css) para chamados ainda na etapa de Abertura, na cor da prioridade
export const pulseClass = (t) => (t.stage === "abertura" && t.status !== "resolvido" ? `ticket-pulse pulse-${t.prioridade}` : "");

// Small boxes do AdminLTE com os indicadores de uma lista de chamados (Dashboard e Chamados)
const SmallBox = ({ bg, value, label, icon, to, testId }) => (
  <div className="col-lg-3 col-6">
    <div className={`small-box ${bg}`} data-testid={testId}>
      <div className="inner">
        <h3>{value}</h3>
        <p>{label}</p>
      </div>
      <div className="icon"><i className={icon} /></div>
      <Link to={to} className="small-box-footer">Mais informações <i className="fas fa-arrow-circle-right" /></Link>
    </div>
  </div>
);

export const TicketStats = ({ tickets, now }) => {
  const abertos = tickets.filter((t) => t.status === "aberto");
  const resolvidos = tickets.length - abertos.length;
  const risco = abertos.filter((t) => ["warning", "breached"].includes(slaInfo(t, now).state)).length;
  const taxa = tickets.length ? Math.round((resolvidos / tickets.length) * 100) : 0;
  return (
    <div className="row">
      <SmallBox testId="kpi-total" bg="bg-info" value={tickets.length} label="Total de chamados" icon="fas fa-ticket-alt" to="/chamados?periodo=todos" />
      <SmallBox testId="kpi-abertos" bg="bg-warning" value={abertos.length} label="Em andamento" icon="fas fa-clock" to="/chamados?stage=andamento" />
      <SmallBox testId="kpi-sla-risco" bg="bg-danger" value={risco} label="Prazo em risco / atrasados" icon="fas fa-exclamation-triangle" to="/chamados?sla=risco" />
      <SmallBox testId="kpi-resolvidos" bg="bg-success" value={<>{taxa}<sup style={{ fontSize: 20 }}>%</sup></>} label={`Resolvidos (${resolvidos})`} icon="fas fa-check-circle" to="/chamados?stage=resolucao" />
    </div>
  );
};
